import { useState } from 'react';
import { cats } from '../../domain/categories.js';
import { keyOfISO } from '../../domain/periods.js';
import { noTags } from '../../lib/text.js';
import { todayISO } from '../../lib/format.js';
import { Seg } from '../../components/ui.jsx';
import { navigate } from '../../app/nav.js';
import { toast, useApp } from '../../store/ui.js';
import { insertTx, recWrite } from '../../store/actions.js';
import { touch } from '../../domain/state.js';
import { FREQS } from '../settings/RecModal.jsx';
import { ScanPanel } from './ScanPanel.jsx';

/** מעבר למסך ההוספה עם הסריקה פתוחה */
export function openScan() {
  navigate('Add');
}

export function AddPage() {
  useApp();
  return (
    <div className="cols wide-side">
      <div className="col">
        <div className="period">
          <div className="sec">הוספה ידנית</div>
          <AddForm />
        </div>
      </div>
      <div className="col">
        <div className="period">
          <div className="card-h">
            <div className="sec" style={{ margin: 0 }}>סריקת דוח <span className="ai-badge">AI</span></div>
          </div>
          <div className="note mb12">קובץ Excel מהבנק או מחברת האשראי (הכי מדויק), PDF, או צילום של הדוח. כפילויות עם מה שכבר נשמר מזוהות לבד.</div>
          <ScanPanel />
        </div>
      </div>
    </div>
  );
}

const EMPTY = () => ({ type: 'expense', description: '', amount: '', cat: 'food', date: todayISO(), source: 'cash', rec: false, day: '', freq: 1 });

function AddForm() {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [shake, setShake] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e && e.target ? (e.target.type === 'checkbox' ? e.target.checked : e.target.value) : e });
  // הכנסה תמיד נכנסת לעו"ש
  const setType = (t) => setF({ ...f, type: t, cat: cats(t)[0].id, source: t === 'income' ? 'cash' : f.source });

  async function add(e) {
    if (e) e.preventDefault();
    if (busy) return;
    const amt = parseFloat(f.amount), desc = noTags(f.description.trim());
    if (!desc || !(amt > 0)) {
      if (f.amount !== '' && !(amt > 0)) toast('הסכום חייב להיות חיובי וגדול מ-0');
      setShake(true); setTimeout(() => setShake(false), 450);
      return;
    }
    setBusy(true);
    try {
      if (f.rec) {
        // בלי יום מפורש: היום של התאריך שנבחר, כי זה היום שבו החיוב ירד
        const day = Math.min(31, Math.max(1, parseInt(f.day, 10) || parseInt(f.date.slice(8, 10), 10) || 1));
        const rb = { description: desc, amount: amt, type: f.type, cat: f.cat, day_of_month: day, source: f.type === 'income' ? 'cash' : f.source };
        const fq = parseInt(f.freq, 10) || 1;
        if (fq > 1) { rb.every_months = fq; rb.start_month = new Date(f.date + 'T12:00:00').getMonth() + 1; }
        // חיוב קבוע בכרטיס חייב להישמר כאשראי, אחרת הוא נספר פעמיים בתזרים
        await recWrite(null, rb);
      }
      await insertTx({ description: desc, amount: amt, type: f.type, cat: f.cat, date: f.date, month: keyOfISO(f.date), source: f.source });
      touch();
      toast(f.rec ? '✅ נשמר + נוסף לחוזרות' : '✅ נשמר');
      setF({ ...EMPTY(), type: f.type, cat: f.cat, source: f.source, date: f.date });
    } catch (e) {
      toast('שגיאה: ' + e.message);
    }
    setBusy(false);
  }

  return (
    <>
      <div className="mb12"><Seg kind="type" value={f.type} onChange={setType} options={[['expense', 'הוצאה', 'exp'], ['income', 'הכנסה', 'inc']]} /></div>
      <form onSubmit={add} noValidate>
        <div className="form-row">
          <div><label className="lbl">תיאור</label><input value={f.description} onChange={set('description')} placeholder="קניות סופר..." /></div>
          <div><label className="lbl">סכום (₪)</label><input type="number" inputMode="decimal" min="0" value={f.amount} onChange={set('amount')} placeholder="0" /></div>
        </div>
        <div className="form-row">
          <div><label className="lbl">קטגוריה</label><select value={f.cat} onChange={set('cat')}>{cats(f.type).map((c) => <option key={c.id} value={c.id}>{c.icon} {c.label}</option>)}</select></div>
          <div><label className="lbl">תאריך</label><input type="date" value={f.date} onChange={set('date')} /></div>
        </div>
        {f.type === 'expense' && <div className="mb12"><label className="lbl">איך שולם</label><Seg value={f.source} onChange={set('source')} options={[['cash', '🏦 עו"ש / מזומן'], ['credit', '💳 כרטיס אשראי']]} /></div>}
        <label className="check">
          <input type="checkbox" checked={f.rec} onChange={set('rec')} />
          <span>{f.type === 'income' ? 'הכנסה קבועה (חוזרת)' : 'חיוב קבוע (חוזר)'}<small>נכנס לתחזית התזרים גם בחודשים הבאים</small></span>
        </label>
        {f.rec && (
          <div className="form-row mt10">
            <div><label className="lbl">יום בחודש</label><input type="number" min="1" max="31" placeholder={String(parseInt(f.date.slice(8, 10), 10) || 1)} value={f.day} onChange={set('day')} /></div>
            <div><label className="lbl">תדירות</label><select value={f.freq} onChange={set('freq')}>{FREQS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></div>
          </div>
        )}
        <button type="submit" className="btn btn-accent btn-full mt12" disabled={busy} style={shake ? { animation: 'shake .4s ease' } : undefined}>
          {busy ? <span className="sp" /> : f.type === 'income' ? 'הוסף הכנסה' : 'הוסף הוצאה'}
        </button>
      </form>
    </>
  );
}
